// @flow

import React, { Component } from 'react';

import { withTranslation, WithTranslation, Trans } from 'react-i18next';
import _ from 'lodash';
import { connect } from 'react-redux';
import { setAuthForm, setFormErrors, unsetError, loadLocalStorageToStore } from '../actions/auth';
import userActions from '../actions/user';
import { fieldError, genericFormError } from '../store/reducers/auth';
import AuthLayout from './layouts/AuthLayout';
import CardSplit from './shared/CardSplit';
import Button from './shared/Button';
import type { AuthState, FormError } from '../store/reducers/auth';
import Text from './shared/Typography';
import Checkbox from './shared/CheckboxInput';
import Login from './pages/login';
import UserCreate from './pages/user-create';

type State = {
  checked: number[],
  showPassword: boolean,
  redirectUri: string
}

type AuthForm = {
  email?: string,
  password?: string
}

type Props = {
  history: History,
  auth: AuthState,
  location: any,
  formError: FormError,
  unsetError: (field: string) => void,
  setFormErrors: (errors: FormError[]) => void,
  fieldError: (field: string) => FormError,
  setAuthForm: (form: AuthForm) => void,
  loadLocalStorageToStore: () => void,
  fetchUser: () => void,
  user: Object,
  partnerInfo: Object,
  connectPartner: () => Promise<void>,
  login: (email: string, password: string) => void,
  signup: (email: string, password: string) => void,
  canDisconnect: Boolean,
  exchangeType: string
} & WithTranslation


class PartnerLogin extends Component<Props, State> {
  state: State = {
    checked: [],
    showPassword: false,
    redirectUri: ''
  }

  props: Props;

  getDisclaimer() {
    const { partnerInfo, t } = this.props;
    const checkboxes = [];
    let body = (
      <Text paragraph>
        <Trans
          // eslint-disable-next-line i18next/no-literal-string
          i18nKey='disclaimer_by_clicking_authorize'
          t={t}
          values={{
            partner_name: partnerInfo.shortName,
            parter_long_name: partnerInfo.longName,
            partner_website: partnerInfo.website
          }}
          components={[
            <strong />
          ]}
        />
      </Text>
    );
    if (this.props.exchangeType && this.props.exchangeType === 'incoming') {
      // The user must consent to their data being sent only in one direction, to Dot Health
      checkboxes.push(t('disclaimer_understand_sharing_PHI'));
      body = (
        <Text paragraph>
          <Trans
            // eslint-disable-next-line i18next/no-literal-string
            i18nKey='disclaimer_by_clicking_authorize_incoming'
            t={t}
            values={{
              partner_name: partnerInfo.shortName,
              parter_long_name: partnerInfo.longName,
              partner_website: partnerInfo.website
            }}
            components={[
              <strong />
            ]}
          />
        </Text>
      );
    } else {
      // The user must consent to bi-directional exchange
      checkboxes.push(t('disclaimer_bi_directional_exchange'));
    }
    if (this.props.canDisconnect) {
      checkboxes.push(t('disclaimer_can_disconnect'));
    }
    return {
      body,
      checkboxes
    };
  }

  componentWillMount() {
    this.props.loadLocalStorageToStore();
    this.resetForm();
    const params = new URLSearchParams(this.props.location.search);
    this.setState({ redirectUri: params.get('redirect_uri') });
    this.setState({ params: params.toString() });
  }

  componentWillReceiveProps(nextProps: Props) {
    if (!this.props.auth.authenticated && nextProps.auth.authenticated) {
      this.props.fetchUser();
    }
  }

  resetForm() {
    this.props.setAuthForm({
      password: ''
    });
    this.props.setFormErrors([]);
  }

  onChange = (event: SyntheticEvent<HTMLInputElement>): void => {
    this.props.unsetError(event.currentTarget.name);
    this.props.setAuthForm({
      [event.currentTarget.name]: event.currentTarget.value || ''
    });
  };

  togglePassword = () => {
    this.setState({
      showPassword: !this.state.showPassword
    });
  };

  toggleCheckbox = (id: number) => {
    let checked = this.state.checked;
    if (checked.includes(id)) checked = checked.filter((item) => item !== id);
    else if (!checked.includes(id)) checked.push(id);
    this.setState({ checked });
  }

  userCreatedProfile = () => {
    return this.props.user && this.props.user.data && this.props.user.data.dob;
  }

  render() {
    const { auth, partnerInfo, t } = this.props;
    const signupTab = 'signup';
    return (
      <React.Fragment>
        {!auth.authenticated && auth.loaded &&
          <Login
            tab={signupTab}
            loginOverride={this.props.login}
            signupOverride={this.props.signup}
            loginHeading={t('login_heading', { partner_name: _.startCase(partnerInfo.shortName) })}
            signupHeading={t('sign_up_heading', { partner_name: _.startCase(partnerInfo.shortName) })}
            noSubHeading
          />
        }
        {auth.authenticated && !this.userCreatedProfile() &&
          <UserCreate callBack={() => this.props.fetchUser()} />
        }
        {auth.authenticated && this.userCreatedProfile() &&
          <AuthLayout className='login login-maple' hideBanner>
            <div className='login-partner__disclaimer'>
              <img
                src={partnerInfo.exchangeImage}
                className='login-partner__disclaimer__image'
                alt={t('partner_img_alt')}
              />
              <Text h2>
                {
                  // eslint-disable-next-line i18next/no-literal-string
                }<Trans i18nKey='logged_in_as' t={t}>
                  <strong>
                    {{
                      first_name: this.props.user.data.first_name,
                      last_name: this.props.user.data.last_name
                    }}
                  </strong>
                </Trans>
              </Text>
              <Text h1 className='login-partner__disclaimer__title'>
                {t('allow_access')}
              </Text>
              {this.props.exchangeType && this.props.exchangeType === 'incoming' &&
                <Text h2>
                  {t('of_my', { partner_name: _.startCase(partnerInfo.shortName) })}
                </Text>
              }
              {(!this.props.exchangeType || this.props.exchangeType !== 'incoming') &&
                <Text h2>
                  {t('btwn_my', { partner_name: _.startCase(partnerInfo.shortName) })}
                </Text>
              }
              <CardSplit
                className='login-partner__disclaimer__legal'
                header={<Text h3>{t('consent_to_disclose_PHI')}</Text>}
                noShadow
              >
                <Text paragraph>{this.getDisclaimer().body}</Text>
                {this.getDisclaimer().checkboxes.map((text, index) => (
                  <Checkbox
                    id={index}
                    label={text}
                    onToggle={this.toggleCheckbox}
                    checked={this.state.checked.includes(index)}
                    key={index}
                  />
                ))}
              </CardSplit>
              <Button
                onClick={this.props.connectPartner}
                isDisabled={this.state.checked.length < this.getDisclaimer().checkboxes.length}
              >
                {t('authorize_partner', { partner_name: _.startCase(partnerInfo.shortName) })}
              </Button>
            </div>
          </AuthLayout>
        }
      </React.Fragment>
    );
  }
}

const mapStateToProps = (appState: { auth: AuthState }) => ({
  auth: appState.auth,
  user: appState.user,
  fieldError: (field) => fieldError(appState.auth, field),
  formError: genericFormError(appState.auth)
});

export default withTranslation('partnerLoginTemplate')(
  connect(mapStateToProps, {
    setAuthForm,
    setFormErrors,
    unsetError,
    loadLocalStorageToStore,
    fetchUser: userActions.fetchUser
  })(PartnerLogin)
);



// WEBPACK FOOTER //
// ./src/components/partner-login-template.js